import { showToast } from 'vant';
import {
  ANDROID_QIPAO_PACKAGE,
  buildAndroidIntentUserHome,
  buildQipaoUserHomeScheme,
  buildWxUserHomeExtinfo,
} from '../utils/userHomeAppLink';
import { isWeChatBrowser } from '../utils/isWeChatBrowser';
import { navigateToDownloadPage } from '../utils/navigateToDownloadPage';

/**
 * 他人主页：「App内打开」唤端逻辑。
 * - 微信浏览器：由页面渲染 `<WechatOpenLaunchApp>`，extinfo 传 `user/home` + `be_user_id`。
 * - 普通浏览器：通过 `qipao://user/home?be_user_id=...`（iOS）/ `intent://...`（Android）唤起，
 *   超时未离开页面则跳下载页兜底。
 * - App 内嵌 WebView：toast 提示已在 App 内。
 *
 * 依赖宿主组件在 `data` 中提供 `beUserId`（他人用户 UUID）。
 */
export default {
  data() {
    return {
      /** @type {{ unified?: (ev: Event) => void } | null} */
      _nativeOpenHandlers: null,
    };
  },
  computed: {
    /**
     * `wx-open-launch-app` 的 extinfo（OpenSDK messageExt）
     * @returns {string}
     */
    wxUserHomeLaunchExtinfo() {
      return buildWxUserHomeExtinfo({ beUserId: this.beUserId });
    },
  },
  methods: {
    /**
     * 是否处于 App 内嵌 WebView（已注入 `QipaoBridge`）
     * @returns {boolean}
     */
    isInAppWebView() {
      const bridge = typeof window !== 'undefined' ? window.QipaoBridge : null;
      return !!(
        bridge &&
        typeof bridge.isSupported === 'boolean' &&
        bridge.isSupported &&
        typeof bridge.openVideoDetail === 'function'
      );
    },
    /**
     * @returns {string}
     */
    getResolvedBeUserId() {
      return String(this.beUserId || '').trim();
    },
    /**
     * 唤端失败时跳转的下载页 URL（同源、带 hash）
     * @returns {string}
     */
    getUserHomeDownloadHref() {
      if (typeof window === 'undefined') return '';
      const u = new URL(window.location.href);
      u.hash = '#/download';
      return u.toString();
    },
    removeUserHomeOpenListeners() {
      if (typeof document === 'undefined' || !this._nativeOpenHandlers) return;
      const h = this._nativeOpenHandlers;
      if (h.unified) {
        document.removeEventListener('visibilitychange', h.unified);
        window.removeEventListener('pagehide', h.unified);
        window.removeEventListener('blur', h.unified);
      }
      this._nativeOpenHandlers = null;
    },
    /**
     * 通过 a 标签触发深链跳转（兼容部分浏览器对 `location.assign` 的拦截）
     * @param {string} href
     */
    triggerUserHomeDeeplink(href) {
      if (typeof window === 'undefined') return;
      try {
        const a = document.createElement('a');
        a.href = href;
        a.rel = 'nofollow noopener';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (_) {
        try {
          window.location.assign(href);
        } catch (e) {
          window.location.href = href;
        }
      }
    },
    /**
     * iframe 兜底（Android 上 intent 后再触一次 scheme，提升唤起率）
     * @param {string} schemeUrl
     */
    tryUserHomeQipaoInIframe(schemeUrl) {
      if (typeof document === 'undefined') return;
      const ifr = document.createElement('iframe');
      ifr.setAttribute('aria-hidden', 'true');
      ifr.title = 'open-app';
      ifr.style.cssText =
        'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
      ifr.src = schemeUrl;
      document.body.appendChild(ifr);
      window.setTimeout(() => {
        try {
          ifr.remove();
        } catch (_) {
          if (ifr.parentNode) ifr.parentNode.removeChild(ifr);
        }
      }, 2000);
    },
    /**
     * 普通浏览器：按 iOS/Android 走 `user/home` 深链，定时未离开页面则跳下载页兜底
     * @returns {void}
     */
    tryOpenUserHomeByDeepLink() {
      if (typeof window === 'undefined') return;

      const beUserId = this.getResolvedBeUserId();
      if (!beUserId) {
        showToast('缺少用户信息');
        return;
      }

      showToast({ message: '正在打开起泡…', duration: 2000 });
      this.removeUserHomeOpenListeners();

      const pkg = ANDROID_QIPAO_PACKAGE;
      const isAndroid = /android/i.test(navigator.userAgent || '');
      const fallbackUrl = this.getUserHomeDownloadHref();
      const params = { beUserId };
      const androidIntent = buildAndroidIntentUserHome(params, pkg, fallbackUrl);
      const qipaoUrl = buildQipaoUserHomeScheme(params);

      const goDownload = () => {
        navigateToDownloadPage(this.$router);
      };

      const inWeChat = isWeChatBrowser();
      const androidFallbackMs = inWeChat ? 1800 : 2600;
      const iosFallbackMs = inWeChat ? 1500 : 2000;

      let leftPage = false;
      const onLeave = () => {
        if (leftPage) return;
        leftPage = true;
        this.removeUserHomeOpenListeners();
      };

      const h = (ev) => {
        if (ev && (ev.type === 'pagehide' || ev.type === 'blur')) {
          onLeave();
          return;
        }
        if (document.hidden || document.visibilityState === 'hidden') onLeave();
      };
      this._nativeOpenHandlers = { unified: h };
      document.addEventListener('visibilitychange', h, { passive: true });
      window.addEventListener('pagehide', h, { passive: true });
      window.addEventListener('blur', h, { passive: true });

      if (isAndroid) {
        this.triggerUserHomeDeeplink(androidIntent);
        window.setTimeout(() => {
          this.tryUserHomeQipaoInIframe(qipaoUrl);
        }, 200);
        window.setTimeout(() => {
          this.removeUserHomeOpenListeners();
          if (!leftPage) {
            if (!inWeChat) {
              showToast({ message: '未检测到 App，可前往下载页安装', duration: 2500 });
            }
            goDownload();
          }
        }, androidFallbackMs);
        return;
      }

      window.location.assign(qipaoUrl);
      window.setTimeout(() => {
        this.removeUserHomeOpenListeners();
        if (!leftPage) {
          if (!inWeChat) {
            showToast({ message: '若未自动打开，可前往 App Store 或下载页', duration: 2000 });
          }
          goDownload();
        }
      }, iosFallbackMs);
    },
    /**
     * 「App内打开」入口：
     * - App 内嵌：toast 提示
     * - 微信内已渲染 `wx-open-launch-app`：toast 提示用户点击下方按钮
     * - 其他：`user/home?be_user_id` 深链唤起
     * @returns {void}
     */
    onOpenUserHomeInApp() {
      if (this.isInAppWebView()) {
        showToast('已在 App 内');
        return;
      }
      if (!this.getResolvedBeUserId()) {
        showToast('缺少用户信息');
        return;
      }
      if (typeof this.shouldUseWechatOpenLaunchTag !== 'undefined' && this.shouldUseWechatOpenLaunchTag) {
        return;
      }
      this.tryOpenUserHomeByDeepLink();
    },
  },
  beforeUnmount() {
    this.removeUserHomeOpenListeners();
  },
};
